import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/context';
import { getRoleHomePath } from '@/auth/routing';
import { validateEmail } from '@/auth/utils';
import { PublicSiteHeader } from '@/components/public/PublicSiteHeader';
import { useTranslation } from 'react-i18next';

const Login: React.FC = () => {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const { t: tErr } = useTranslation('errors');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordResetSuccess = searchParams.get('recovery') === 'password-reset-success';

  useEffect(() => {
    if (state.isAuthenticated && state.user) {
      if (state.user.role === 'subscriber' && state.user.status !== 'active') {
        navigate('/dashboard/pending', { replace: true });
      } else {
        navigate(getRoleHomePath(state.user.role), { replace: true });
      }
    }
  }, [state.isAuthenticated, state.user, navigate]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!validateEmail(email)) {
      setError(tErr('enterValidEmail'));
      return;
    }

    setIsSubmitting(true);
    try {
      await login({ email, password });
    } catch (err) {
      const message = err instanceof Error ? err.message : tErr('loginFailed');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      <PublicSiteHeader />
      <main className="flex min-h-[calc(100vh-65px)] items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-2xl border border-[#E2E8F0] bg-white p-10 shadow-sm">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-black text-[#0F172A]">{t('secureAccess')}</h1>
            <p className="mt-2 text-sm text-[#64748B]">
              {t('subscriberAccessOnly')}
            </p>
          </div>

          {passwordResetSuccess && (
            <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {t('passwordUpdatedSignIn')}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#64748B]">{t('email')}</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-[#0F172A] outline-none transition-colors placeholder:text-[#CBD5E1] focus:border-[#E10613]"
                placeholder="name@bank.com"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#64748B]">{t('password')}</label>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-[#0F172A] outline-none transition-colors placeholder:text-[#CBD5E1] focus:border-[#E10613]"
                placeholder="••••••••"
              />
              <div className="mt-2 text-right">
                <Link
                  to={`/forgot-password?returnTo=${encodeURIComponent('/login')}${email ? `&email=${encodeURIComponent(email)}` : ''}`}
                  className="text-xs text-[#E10613] underline underline-offset-4 hover:text-[#C1121F]"
                >
                  {t('forgotPassword')}
                </Link>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="h-12 w-full rounded-lg bg-[#E10613] font-black text-white transition-colors hover:bg-[#C1121F] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? t('signingIn') : t('signIn')}
            </button>
          </form>

          <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 text-xs text-[#64748B]">
            Demo subscriber accounts: analyst@accessbank.com, manager@gtbank.com
          </div>
          <div className="mt-4 text-center text-xs text-[#64748B]">
            {t('newInstitution')}{' '}
            <Link to="/get-started" className="text-[#E10613] underline underline-offset-4 hover:text-[#C1121F]">
              {t('getStarted')}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
